import { useEffect, useState } from 'react'

import { fetchSubscriptions } from '@/reducers/subscription.slice'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { fetchAllocationRulesIfNeeded } from '@/actions/allocationRuleActions'
import {
  getCurrentUser,
  selectMembershipStart,
} from '@/selectors/user.selectors'
import { Loader } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { useShowTaxKickoffCard } from './useShowTaxKickoffCard'
import { getOrdinal } from '@/utils/dateHelpers'
import { DateTime } from 'luxon'
import { HeardiversaryHeader } from './HeardiversaryHeader'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'

import { fetchUserTransactions } from '@/features/transactions/transactions.slice'
import { fetchTransactionCategoriesIfNeeded } from '@/features/reports/reports.slice'
import { fetchCurrentUserDetailsIfNeeded } from '@/actions/userActions'
import { fetchFinancialProfileActionItems } from './financialProfileActionItems.slice'
import { fetchUserActionItemsIfNeeded } from './user-action-items/lib/userActionItems.slice'

import { fetchSCorpElectionStatus } from './s-corp-upsell/sCorpActions'

import { MaximizeTaxDeductionsCard } from '@/features/user/dashboard/insights-cards/MaximizeTaxDeductionsCard'
import { FeatureAnnouncementCard } from '@/features/user/dashboard/feature-announcement/FeatureAnnouncementCard'
import { MobileNavAnnouncementCard } from '@/features/user/dashboard/sidebar-nav-announcement/MobileNavAnnouncementCard'
import { PageHeader } from '@/components/PageHeader'
import { SmsConsentButton } from '@/features/user/settings/sections/account-details-card/sms-consent/SmsConsentButton'
import { UserImportantDatesCard } from './user-important-dates'
import { UserActionItemsCard } from './user-action-items'
import { SummaryActions } from './SummaryActions'
import { WebinarCard } from '@/features/user/dashboard/cards/WebinarCard'
import { PayrollCard } from '@/features/user/dashboard/cards/PayrollCard'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'

import { fetchUserDocumentCategoriesIfNeeded } from '@/features/admin/user-document-categories/userDocumentCategories.slice'
import { fetchPayrollProfileIfNeeded } from '@/features/payroll/payrollActions'
import { selectShouldShowPayrollUpsellCard } from '@/features/payroll/payroll.selectors'
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/features/admin/annual-tax-details/annualTaxDetails.slice'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { TaxSeasonKickoffEntryCard } from '@/features/user/dashboard/cards/TaxSeasonKickoffEntryCard'
import { MonthToDateIncomeCard } from '@/features/user/dashboard/insights-cards/MonthToDateIncomeCard'
import { YearToDateProfitLossCard } from '@/features/user/dashboard/insights-cards/YearToDateProfitLossCard'
import { GetHelpCard } from '@/features/user/dashboard/cards/GetHelpCard'

import { fetchAllEoyReviewStepsIfNeeded } from '@/features/taxes/annual-taxes/tax-checklist/shared/review-steps-and-progresses/allEoyReviewSteps.slice'
import { fetchAnnualTaxFilingFormsIfNeeded } from '@/features/taxes/annual-taxes/annualTaxFilingForms.slice'
import { fetchAnnualTaxFilingsIfNeeded } from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import { fetchUserEoyReviewProgress } from '@/features/taxes/annual-taxes/tax-checklist/shared/review-steps-and-progresses/userEndOfYearReviewProgress.slice'
import { fetchUserYearEndModuleStatuses } from '@/features/year-end-module-status/yearEndModuleStatus.slice'
import {
  selectShowLateFilingAlert,
  selectCurrentAnnualTaxFiling,
} from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import UsersnapNewUserExperienceAndExpectations from '@/features/taxes/annual-taxes/UsersnapNewUserExperience'
import TaxConsultationCSATSurvey from '@/features/taxes/advisory/TaxConsultationSurvey'
import UsersnapTaxSeasonNPSSurvey from '@/features/taxes/annual-taxes/UsersnapTaxSeasonNPSSurvey'
import UsersnapNPSWebsite from '@/features/taxes/annual-taxes/UsersnapNpsWebsite'
import { LateFilingAlert } from '@/features/taxes/annual-taxes/components/LateFilingAlert'
import { TypographyStyles } from '@/styles'

export const MainDashboard = () => {
  const dispatch = useAppDispatch()
  const [fetching, setFetching] = useState(true)
  const currentUser = useReselector(getCurrentUser)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  const shouldDisplayPayrollUpsellCard = useReselector(
    selectShouldShowPayrollUpsellCard
  )

  const membershipStart = useReselector(selectMembershipStart)
  const taxYear = useReselector(selectCurrentAnnualTaxYear)
  const showLateFilingAlert = useReselector(selectShowLateFilingAlert)

  const currentAnnualTaxFiling = useReselector(selectCurrentAnnualTaxFiling)
  const isUserDisqualifiedFromAnnualTaxes = Boolean(
    currentAnnualTaxFiling?.disqualifiedAt
  )
  const showTaxKickoffCard = useShowTaxKickoffCard()

  useEffect(() => {
    if (currentUser?.id) {
      dispatch(fetchSCorpElectionStatus(currentUser.id))
    }
  }, [dispatch, currentUser?.id])

  useEffect(() => {
    async function batchFetch() {
      await Promise.all([
        dispatch(fetchFinancialProfileActionItems()),
        dispatch(fetchUserTransactions(true)), // This should be remove once we migrate transaction related insights to the api
        dispatch(fetchTransactionCategoriesIfNeeded()),
        dispatch(fetchFinancialAccountsIfNeeded()),
        dispatch(fetchAllocationRulesIfNeeded()),
        dispatch(fetchUserDocuments()),
        dispatch(fetchUserDocumentCategoriesIfNeeded()),
        dispatch(fetchCurrentUserDetailsIfNeeded(true)),
        dispatch(
          fetchUserActionItemsIfNeeded({
            forceFetch: true,
          })
        ),
        dispatch(fetchPayrollProfileIfNeeded()),
        //remove the below fetches if LateFilingAlert is ever removed
        dispatch(fetchAnnualTaxFilingFormsIfNeeded()),
        dispatch(fetchAnnualTaxFilingsIfNeeded()),
        dispatch(fetchAllAnnualTaxDetailsIfNeeded()),
        dispatch(fetchAllEoyReviewStepsIfNeeded()),
        dispatch(fetchSubscriptions()),
        dispatch(fetchUserYearEndModuleStatuses()),
      ])

      setFetching(false)
    }

    batchFetch()
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchUserEoyReviewProgress(taxYear))
  }, [dispatch, taxYear])

  if (fetching) {
    return <Loader />
  }

  const pageHeader = (() => {
    if (!membershipStart) return `Welcome back, ${currentUser?.firstName}!`
    const today = DateTime.now().startOf('day')
    const joined = DateTime.fromISO(membershipStart)
    // Check both this year and last year's anniversary to handle 30-day
    // windows that cross a year boundary (e.g. Dec 20 join, viewed Jan 5)
    const candidates = [
      joined.set({ year: today.year }),
      joined.set({ year: today.year - 1 }),
    ]
    for (const anniversary of candidates) {
      const daysSince = today.diff(anniversary.startOf('day'), 'days').days
      const years = anniversary.year - joined.year
      if (daysSince >= 0 && daysSince < 30 && years > 0) {
        return (
          <HeardiversaryHeader
            firstName={currentUser?.firstName ?? ''}
            ordinal={getOrdinal(years)}
          />
        )
      }
    }
    return `Welcome back, ${currentUser?.firstName}!`
  })()

  return (
    <>
      {!isMobile && (
        <PageHeader
          header={pageHeader}
          size={TypographyStyles.DISPLAY_XS}
          extraContent={<SmsConsentButton />}
        />
      )}

      <div className="flex flex-col space-y-0">
        <MobileNavAnnouncementCard />
        {showTaxKickoffCard === false && <FeatureAnnouncementCard />}
        <div className="space-y-4 mt-4">
          <SummaryActions />
          {showLateFilingAlert && !isUserDisqualifiedFromAnnualTaxes && (
            <LateFilingAlert />
          )}
          {showTaxKickoffCard && <TaxSeasonKickoffEntryCard />}
        </div>
        <MonthToDateIncomeCard />
        <div className="mt-2">
          <YearToDateProfitLossCard />
        </div>
        <div className="mt-2">
          <MaximizeTaxDeductionsCard />
        </div>

        <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
          <div className="w-full md:w-11/16">
            <UserActionItemsCard />
          </div>

          <div className="w-full md:w-5/16 space-y-4">
            {shouldDisplayPayrollUpsellCard && <PayrollCard />}
            <UserImportantDatesCard />
            <WebinarCard />
            <GetHelpCard />
          </div>
        </div>
        <UsersnapTaxSeasonNPSSurvey />
        <UsersnapNewUserExperienceAndExpectations />
        <UsersnapNPSWebsite />
        <TaxConsultationCSATSurvey />
      </div>
    </>
  )
}
